import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  TrendingUp, TrendingDown, DollarSign, Truck, Users, Clock,
  Loader2, RefreshCw, AlertTriangle, ArrowUpRight, ArrowDownRight,
  BarChart3, MapPin, Percent, Shield
} from 'lucide-react';
import { Button } from '../ui/button';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => {
  if (!v && v !== 0) return '0';
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}Md`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return v.toLocaleString('fr-FR');
};

const MetricCard = ({ title, value, subValue, icon: Icon, statusLabel, progress, className = '' }) => {
  const statusStyles = {
    'Sain': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'Attention': 'bg-amber-100 text-amber-700 border-amber-200',
    'Critique': 'bg-red-100 text-red-700 border-red-200',
    'Info': 'bg-blue-100 text-blue-700 border-blue-200',
  };
  const barColor = progress >= 80 ? 'bg-emerald-500' : progress >= 50 ? 'bg-amber-400' : progress > 0 ? 'bg-blue-400' : 'bg-slate-300';
  const iconBg = progress >= 80 ? 'bg-emerald-50' : progress >= 50 ? 'bg-amber-50' : 'bg-blue-50';
  const iconColor = progress >= 80 ? 'text-emerald-500' : progress >= 50 ? 'text-amber-500' : 'text-blue-500';

  return (
    <Card className={`bg-white border border-slate-200 shadow-sm overflow-hidden ${className}`}>
      <CardContent className="p-4 pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-1.5 mb-1">
              <p className="text-xs font-medium text-slate-500">{title}</p>
              {statusLabel && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${statusStyles[statusLabel] || statusStyles['Info']}`}>
                  {statusLabel}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {subValue && <p className="text-xs text-gray-500 mt-0.5">{subValue}</p>}
          </div>
          <div className={`p-2 rounded-lg ${iconBg}`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
      {progress !== undefined && (
        <div className="px-4 pb-3">
          <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
            <div className={`h-full rounded-full ${barColor} transition-all duration-700`} style={{ width: `${Math.min(progress, 100)}%` }} />
          </div>
        </div>
      )}
    </Card>
  );
};

const ProfitabilityKPIs = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const headers = { Authorization: 'Bearer cookie-auth' };

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/dashboard/profitability-kpis`, { headers });
      setData(res.data);
    } catch (e) {
      console.error('Profitability KPIs error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-center text-gray-500 py-16">Impossible de charger les données de rentabilité</p>;
  }

  const { marginSummary: ms, carrierDependency: cd, clientDependency: cld, commercialCycle: cc, topRoutes, bottomRoutes, carriers, clients, lowMarginAlerts } = data;

  const marginStatus = ms.avgMarginPct >= 25 ? 'Sain' : ms.avgMarginPct >= 15 ? 'Attention' : 'Critique';
  const carrierStatus = cd.alert ? 'Critique' : 'Sain';
  const clientStatus = cld.alert ? 'Critique' : 'Sain';
  const cycleStatus = cc.avgCreationToPaymentDays !== null ? (cc.avgCreationToPaymentDays <= 15 ? 'Sain' : cc.avgCreationToPaymentDays <= 30 ? 'Attention' : 'Critique') : 'Info';

  // Chart data for routes
  const routeChartData = [...(topRoutes || []), ...(bottomRoutes || [])].map(r => {
    const parts = r.route.split(' → ');
    const shortRoute = parts.map(p => {
      const city = p.split(',')[0].replace('NANO, Yopougon Zone Industrielle, ', '').replace('Carrefour Phaiblaiss, ', '');
      return city.length > 12 ? city.substring(0, 12) + '..' : city;
    }).join(' → ');
    return { ...r, shortRoute };
  });

  return (
    <div className="space-y-6" data-testid="profitability-kpis">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-emerald-500" />
          <h2 className="text-lg font-semibold text-gray-800">Rentabilité</h2>
          <Badge variant="outline" className="text-xs">{ms.totalOpsWithMargin} opérations analysées</Badge>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Marge Brute Moy."
          value={`${ms.avgMarginPct}%`}
          subValue={`${fmt(ms.totalMarginAbs)} FCFA de marge totale`}
          icon={Percent}
          statusLabel={marginStatus}
          progress={ms.avgMarginPct}
        />
        <MetricCard
          title="Marge Min / Max"
          value={`${ms.minMarginPct}% — ${ms.maxMarginPct}%`}
          subValue={`${ms.lowMarginCount} ops < 15% de marge`}
          icon={BarChart3}
          statusLabel={ms.lowMarginCount > 0 ? 'Attention' : 'Sain'}
          progress={100 - (ms.lowMarginCount / ms.totalOpsWithMargin) * 100}
        />
        <MetricCard
          title="Dép. Transporteur"
          value={`${cd.topCarrierSharePct}%`}
          subValue={`${cd.topCarrier} (${cd.totalCarriers} transporteurs)`}
          icon={Truck}
          statusLabel={carrierStatus}
          progress={100 - cd.topCarrierSharePct}
        />
        <MetricCard
          title="Dép. Client"
          value={`${cld.topClientSharePct}%`}
          subValue={`${cld.topClient} (${cld.totalClients} clients)`}
          icon={Users}
          statusLabel={clientStatus}
          progress={100 - cld.topClientSharePct}
        />
        <MetricCard
          title="Cycle Commercial"
          value={cc.avgCreationToPaymentDays !== null ? `${cc.avgCreationToPaymentDays}j` : 'N/A'}
          subValue={cc.sampleSize > 0 ? `Basé sur ${cc.sampleSize} ops payées` : 'Aucune donnée'}
          icon={Clock}
          statusLabel={cycleStatus}
          progress={cc.avgCreationToPaymentDays !== null ? Math.max(0, 100 - (cc.avgCreationToPaymentDays / 30) * 100) : 0}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Margin by Route Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-5 w-5 text-emerald-500" />
              Marge par Route
            </CardTitle>
            <CardDescription>Top et bottom routes par rentabilité</CardDescription>
          </CardHeader>
          <CardContent>
            {routeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={routeChartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} fontSize={11} />
                  <YAxis type="category" dataKey="shortRoute" width={120} fontSize={10} tick={{ fill: '#475569' }} />
                  <Tooltip
                    formatter={(v, n, p) => [`${v}% (${p.payload.count} ops)`, 'Marge']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                  />
                  <Bar dataKey="avgMarginPct" radius={[0, 4, 4, 0]} maxBarSize={24}>
                    {routeChartData.map((entry, i) => (
                      <Cell key={i} fill={entry.avgMarginPct >= 25 ? '#10b981' : entry.avgMarginPct >= 15 ? '#f59e0b' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-400 py-8">Aucune donnée de route</p>
            )}
          </CardContent>
        </Card>

        {/* Carriers Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-500" />
              Transporteurs
            </CardTitle>
            <CardDescription>Volume, part de marché et marge moyenne</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {carriers.map((c, i) => (
                <div key={c.name} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.count} ops &middot; {fmt(c.totalRevenue)} FCFA</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-slate-800">{c.sharePct}%</p>
                    <p className={`text-xs font-medium ${c.avgMarginPct >= 25 ? 'text-emerald-600' : c.avgMarginPct >= 15 ? 'text-amber-600' : 'text-red-600'}`}>
                      marge {c.avgMarginPct}%
                    </p>
                  </div>
                  <div className="w-16 flex-shrink-0">
                    <div className="w-full bg-slate-200 rounded-full h-1.5">
                      <div className={`h-full rounded-full ${c.sharePct > 40 ? 'bg-red-400' : 'bg-blue-400'}`} style={{ width: `${c.sharePct}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Clients Profitability */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-500" />
              Rentabilité par Client
            </CardTitle>
            <CardDescription>Revenus, marge et part de marché</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[240px] overflow-y-auto">
              {clients.map((c, i) => (
                <div key={c.name} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.count} ops &middot; {c.sharePct}% du volume</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-mono font-bold text-slate-800">{fmt(c.totalRevenue)}</p>
                    <p className={`text-xs font-medium ${c.avgMarginPct >= 25 ? 'text-emerald-600' : c.avgMarginPct >= 15 ? 'text-amber-600' : 'text-red-600'}`}>
                      marge {c.avgMarginPct}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Low Margin Alerts */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Alertes Marge Faible
            </CardTitle>
            <CardDescription>Opérations avec marge inférieure à 15%</CardDescription>
          </CardHeader>
          <CardContent>
            {lowMarginAlerts.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-emerald-600">
                <Shield className="h-5 w-5 mr-2" />
                <span className="text-sm font-medium">Aucune alerte — toutes les marges sont saines</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-[240px] overflow-y-auto">
                {lowMarginAlerts.map((op, i) => (
                  <div key={op.id || i} className="flex items-center gap-3 p-2.5 rounded-lg bg-red-50 border border-red-100">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{op.route}</p>
                      <p className="text-xs text-slate-500">{op.carrierName} &middot; {op.companyName}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-red-600">{op.marginPct}%</p>
                      <p className="text-xs text-slate-500">{fmt(op.price)} FCFA</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfitabilityKPIs;
